import type { SolutionPageContent } from '../../types/solution'

export const healthcareSolution: SolutionPageContent = {
  slug: 'healthcare',
  hero: {
    headline: 'Run your pharmacy or clinic with fewer surprises',
    subheadline: 'Manage pharmacy sales, clinic services, stock, and payments — with expiry dates you can trust.',
    card: {
      type: 'list',
      title: 'Expiring soon',
      badge: { label: '3 items', tone: 'warning' },
      rows: [
        { label: 'Amoxicillin 500mg', sublabel: 'Batch A21 · 14 boxes', value: '18 days', tone: 'negative' },
        { label: 'Paracetamol syrup', sublabel: 'Batch P07 · 22 bottles', value: '41 days', tone: 'warning' },
        { label: 'ORS sachets', sublabel: 'Batch O33 · 60 pcs', value: '58 days' },
      ],
      action: 'Plan promotion',
    },
  },
  challenges: [
    {
      quote: 'Medicines expire on the shelf before we sell them.',
      detail: 'By the time we notice, it’s money straight into the bin.',
    },
    {
      quote: 'Patients pay part now, part with insurance, part later.',
      detail: 'Keeping track of who owes what is a full-time job.',
    },
    {
      quote: 'We run out of essential medicines without warning.',
      detail: 'A patient leaves without what they need, and trust takes a hit.',
    },
  ],
  helps: [
    {
      solves: 'Medicines expire on the shelf',
      title: 'Batches and expiry dates, tracked for you',
      copy: 'Record batch numbers and expiry dates as stock arrives. Kayko warns you early so you can sell, return, or rotate first.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Stock by expiry',
          rows: [
            { label: 'Under 30 days', value: 'RWF 84,000', pct: 12, tone: 'negative' },
            { label: '30–90 days', value: 'RWF 312,000', pct: 38, tone: 'warning' },
            { label: 'Over 90 days', value: 'RWF 2.4M', pct: 90, tone: 'positive' },
          ],
        },
      },
    },
    {
      solves: 'Patients pay in parts',
      title: 'Split payments and patient balances',
      copy: 'Take part by mobile money, part by insurance, and record what’s left as a balance. Every patient’s account is always clear.',
      product: 'customers',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Consultation · E. Nsengimana',
          rows: [
            { label: 'Consultation', value: 'RWF 10,000' },
            { label: 'Lab test', value: 'RWF 6,000' },
            { label: 'Paid · Insurance', value: '− RWF 12,800', tone: 'positive' },
            { label: 'Paid · Mobile money', value: '− RWF 2,000', tone: 'positive' },
          ],
          footer: { label: 'Balance', value: 'RWF 1,200' },
        },
      },
    },
    {
      solves: 'We run out of essential medicines',
      title: 'Reorder before you run out',
      copy: 'Set reorder levels for essentials and turn low-stock items into a purchase order for your supplier in a few taps.',
      product: 'purchases',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Purchase order #PO-0231',
          meta: 'Kigali Medical Supplies',
          badge: { label: 'Draft', tone: 'default' },
          rows: [
            { label: 'Paracetamol 500mg', sublabel: '40 boxes', value: 'RWF 48,000' },
            { label: 'ORS sachets', sublabel: '200 pcs', value: 'RWF 30,000' },
          ],
          footer: { label: 'Total', value: 'RWF 78,000' },
          action: 'Send to supplier',
        },
      },
    },
  ],
  journey: {
    heading: 'A day with Kayko',
    subheading: 'How a neighbourhood pharmacy runs its day.',
    items: [
      { time: '7:30', title: 'Check expiry alerts', copy: 'See which batches need attention first.' },
      { time: '8:00', title: 'Serve patients', copy: 'Sell medicines and services with EBM receipts.' },
      { time: '12:00', title: 'Take split payments', copy: 'Insurance, mobile money, and balances.' },
      { time: '15:00', title: 'Reorder essentials', copy: 'Send a purchase order to your supplier.' },
      { time: '20:00', title: 'Close the day', copy: 'Review sales and open patient balances.' },
    ],
  },
  results: [
    { value: '−70%', label: 'less stock lost to expiry' },
    { value: '0', label: 'essential medicines out of stock last month' },
    { value: '100%', label: 'of patient balances tracked' },
  ],
  testimonial: {
    quote: 'We used to throw away expired stock every month. Now Kayko tells us early, and we act before it’s too late.',
    name: 'Alice Uwamahoro',
    role: 'Pharmacist & Owner',
    business: 'Ubuzima Pharmacy',
    location: 'Nyamirambo, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/290df7a2-5654-48ae-b59a-6a81a29ab1f5.jpg',
    metric: { value: '−70%', label: 'less stock lost to expiry' },
  },
  products: ['sales-pos', 'inventory', 'purchases', 'customers'],
  plan: { name: 'Starter', audience: 'pharmacies and clinics' },
  faq: [
    {
      question: 'Can I track batch numbers and expiry dates?',
      answer: 'Yes. Record batch and expiry when stock arrives, and Kayko alerts you before items expire.',
    },
    {
      question: 'Can I sell both medicines and clinic services?',
      answer: 'Yes. Add products and services such as consultations or lab tests to the same bill.',
    },
    {
      question: 'Can patients pay partly with insurance?',
      answer: 'Yes. Record the insurance portion and the patient portion separately, and track any balance left.',
    },
    {
      question: 'Is patient information kept private?',
      answer: 'Only staff you authorise can see patient records, and all data is encrypted. See our Security page for details.',
    },
    {
      question: 'Are receipts EBM compliant?',
      answer: 'Yes. Every sale issues an RRA EBM-compliant receipt, printed or sent digitally.',
    },
  ],
  related: ['retail', 'manage-inventory', 'services'],
  cta: {
    headline: 'Keep the right medicines on your shelves',
    subheadline: 'Set up in minutes. Local support in Kigali when you need it.',
  },
}
